import type { ValueOf } from "../utils/types";
import type { DbEnv } from "./db";

export const HIGHARC_DOMAIN = "higharc.com";

export const ENVIRONMENTS: Record<EnvironmentKey | "master", EnvironmentKey> = {
  docker_test: "docker_test",
  local: "local",
  local_test: "local_test",
  sandbox1: "sandbox1",
  sandbox2: "sandbox2",
  sandbox3: "sandbox3",
  sandbox4: "sandbox4",
  sandbox5: "sandbox5",
  sandbox6: "sandbox6",
  sandbox7: "sandbox7",
  sandbox8: "sandbox8",
  sandbox9: "sandbox9",
  sandbox10: "sandbox10",
  sandbox11: "sandbox11",
  sandbox12: "sandbox12",
  sandbox13: "sandbox13",
  sandbox14: "sandbox14",
  sandbox15: "sandbox15",
  sandbox16: "sandbox16",
  demo1: "demo1",
  pr: "pr",
  develop: "develop",
  qa: "qa",
  stage: "stage",
  prod: "prod",
  master: "prod", // In the event Git branch is mistakenly used to determine URL
} as const;

export const BASE_URLS: Record<EnvironmentKey | "master", string> = {
  docker_test: "http://localhost:3000",
  local: "http://localhost:3000",
  local_test: "http://localhost:3000",
  sandbox1: "https://app.sandbox1.higharc.com",
  sandbox2: "https://app.sandbox2.higharc.com",
  sandbox3: "https://app.sandbox3.higharc.com",
  sandbox4: "https://app.sandbox4.higharc.com",
  sandbox5: "https://app.sandbox5.higharc.com",
  sandbox6: "https://app.sandbox6.higharc.com",
  sandbox7: "https://app.sandbox7.higharc.com",
  sandbox8: "https://app.sandbox8.higharc.com",
  sandbox9: "https://app.sandbox9.higharc.com",
  sandbox10: "https://app.sandbox10.higharc.com",
  sandbox11: "https://app.sandbox11.higharc.com",
  sandbox12: "https://app.sandbox12.higharc.com",
  sandbox13: "https://app.sandbox13.higharc.com",
  sandbox14: "https://app.sandbox14.higharc.com",
  sandbox15: "https://app.sandbox15.higharc.com",
  sandbox16: "https://app.sandbox16.higharc.com",
  demo1: "https://app.demo1.higharc.com",
  pr: process.env.PUBLIC_BASE_URL ?? "https://pr.higharc.com",
  develop: "https://app.dev.higharc.com",
  qa: "https://app.qa.higharc.com",
  stage: "https://app.stage.higharc.com",
  prod: `https://app.${HIGHARC_DOMAIN}`,
  master: `https://app.${HIGHARC_DOMAIN}`, // In the event Git branch is used to determine URL
} as const;

/**
 * Branches that correspond to a deployment.
 */
export type ReleaseBranch =
  | "sandbox1"
  | "sandbox2"
  | "sandbox3"
  | "sandbox4"
  | "sandbox5"
  | "sandbox6"
  | "sandbox7"
  | "sandbox8"
  | "sandbox9"
  | "sandbox10"
  | "sandbox11"
  | "sandbox12"
  | "sandbox13"
  | "sandbox14"
  | "sandbox15"
  | "sandbox16"
  | "demo1"
  | "develop"
  | "qa"
  | "stage"
  | "master";

/**
 * These map to our Terraform environments.
 */
export const ENV_LIST = [
  "docker_test",
  "local",
  "local_test",
  "sandbox1",
  "sandbox2",
  "sandbox3",
  "sandbox4",
  "sandbox5",
  "sandbox6",
  "sandbox7",
  "sandbox8",
  "sandbox9",
  "sandbox10",
  "sandbox11",
  "sandbox12",
  "sandbox13",
  "sandbox14",
  "sandbox15",
  "sandbox16",
  "demo1",
  "pr",
  "develop",
  "qa",
  "stage",
  "prod",
] as const;
export type EnvironmentKey = (typeof ENV_LIST)[number];

export const CONTENT_BASE_URLS: Record<EnvironmentKey | "master", string> = {
  docker_test: "https://content.localdev.higharc.com",
  local: "https://content.localdev.higharc.com",
  local_test: "https://content.localdev.higharc.com",
  sandbox1: "https://content.sandbox1.higharc.com",
  sandbox2: "https://content.sandbox2.higharc.com",
  sandbox3: "https://content.sandbox3.higharc.com",
  sandbox4: "https://content.sandbox4.higharc.com",
  sandbox5: "https://content.sandbox5.higharc.com",
  sandbox6: "https://content.sandbox6.higharc.com",
  sandbox7: "https://content.sandbox7.higharc.com",
  sandbox8: "https://content.sandbox8.higharc.com",
  sandbox9: "https://content.sandbox9.higharc.com",
  sandbox10: "https://content.sandbox10.higharc.com",
  sandbox11: "https://content.sandbox11.higharc.com",
  sandbox12: "https://content.sandbox12.higharc.com",
  sandbox13: "https://content.sandbox13.higharc.com",
  sandbox14: "https://content.sandbox14.higharc.com",
  sandbox15: "https://content.sandbox15.higharc.com",
  sandbox16: "https://content.sandbox16.higharc.com",
  demo1: "https://content.demo1.higharc.com",
  pr: process.env.PUBLIC_CONTENT_BASE_URL ?? "https://content.pr.higharc.com",
  develop: "https://content.dev.higharc.com",
  qa: "https://content.qa.higharc.com",
  stage: "https://content.stage.higharc.com",
  prod: `https://content.${HIGHARC_DOMAIN}`,
  master: `https://content.${HIGHARC_DOMAIN}`, // In the event Git branch is used to determine URL
} as const;

export const DB_ENV_URL_MAP = {
  // db dumps
  develop_local_dump: BASE_URLS.local,
  stage_local_dump: BASE_URLS.local,
  prod_local_dump: BASE_URLS.local,

  // tests
  local_test: BASE_URLS.local_test,
  docker_test: BASE_URLS.docker_test,

  // app url
  local: BASE_URLS.local,
  sandbox1: BASE_URLS.sandbox1,
  sandbox2: BASE_URLS.sandbox2,
  sandbox3: BASE_URLS.sandbox3,
  sandbox4: BASE_URLS.sandbox4,
  sandbox5: BASE_URLS.sandbox5,
  sandbox6: BASE_URLS.sandbox6,
  sandbox7: BASE_URLS.sandbox7,
  sandbox8: BASE_URLS.sandbox8,
  sandbox9: BASE_URLS.sandbox9,
  sandbox10: BASE_URLS.sandbox10,
  sandbox11: BASE_URLS.sandbox11,
  sandbox12: BASE_URLS.sandbox12,
  sandbox13: BASE_URLS.sandbox13,
  sandbox14: BASE_URLS.sandbox14,
  sandbox15: BASE_URLS.sandbox15,
  sandbox16: BASE_URLS.sandbox16,
  demo1: BASE_URLS.demo1,
  pr: BASE_URLS.pr,
  develop: BASE_URLS.develop,
  qa: BASE_URLS.qa,
  stage: BASE_URLS.stage,
  prod: BASE_URLS.prod,
} as const;

export type Server = ValueOf<typeof ENVIRONMENTS>;

export interface ServerEnvironment {
  server: Server;
  cognito: Server;
  db: DbEnv;
  localhost: boolean;
  branch: string;
  tag: string;
  commit: string;
  developmentMode: boolean;
}

export const isServer = typeof window === "undefined";

export const TEST_E2E_MODE = process.env.TEST_E2E_MODE === "true";
